import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import HomeDashboardLayout from "../HomeDashboardLayout";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../DashboardFavoriteExam.css";
import "../../DashboardQuestionBank.css";
import HeaderProfileSection from "../HeaderProfileSection";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useFlashCardActions, useFlashCardDeck, useFlashCardProgress } from "../../hooks/useFlashCards";
import { buildQuestionBankFlowState, getStoredQuestionBankFlowState, persistQuestionBankFlowState } from "../../questionBank/qbankFlowState";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { speakWithIndianVoices } from "../../utils/indianSpeech";

import "./StartFlashCardd.css";
import "./FlashCard.css";
import { FlashCardDeckShimmer } from "./FlashCardShimmer";
import { FLASH_CARD_ROUTES } from "./flashCardRoutes";

/* =========================================================
   FLASHCARD DATA
========================================================= */

const flashCardData = [
  {
    id: 1,
    front:
      "The Civil Services (Preliminary) Examination also serves as a screening test for which other examination?",
    back: "Indian Forest Service Examination",
    color: "cyan",
  },
  {
    id: 2,
    front: "देह, नल, और क्या ______ शब्द के पर्यायवाची हैं?",
    back: "शरीर",
    color: "purple",
  },

  {
    id: 3,
    front: "शब्द 'क्षति' का विलोम शब्द क्या है?",
    back: "लाभ",
    color: "pink",
  },

  {
    id: 4,
    front: "What is the capital of India?",
    back: "New Delhi",
    color: "orange",
  },

  {
    id: 5,
    front: "What is the largest planet in our solar system?",
    back: "Jupiter",
    color: "blue",
  },

  {
    id: 6,
    front: "What is the chemical symbol of Gold?",
    back: "Au",
    color: "green",
  },
];

const FlashCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(() => buildQuestionBankFlowState(location.state, getStoredQuestionBankFlowState()), [location.state]);
  const stateId = resolveQuestionBankStateId({ locationState: flowState, sidebarData }) || 13;
  const subjectId = Number(flowState?.selectedSubject?.id || flowState?.subject_id || 0);
  const cardProgress = flowState?.card_progress || "";
  const requestParams = useMemo(() => ({
    states_id: stateId,
    category_id: 9,
    subject_id: subjectId,
    language: flowState?.language || "EN",
    ...contentTopicRequest(flowState),
    ...(cardProgress ? { card_progress: cardProgress } : {}),
  }), [cardProgress, flowState, stateId, subjectId]);
  const deckQuery = useFlashCardDeck(requestParams, Boolean(subjectId));
  const progressQuery = useFlashCardProgress({ ...requestParams, card_progress: undefined }, Boolean(subjectId));
  const { markRead, bookmark, storeView } = useFlashCardActions();
  const summary = progressQuery.data?.payload?.summary || {};
  const progress = Number(summary.progress_percent || 0);
  /* =========================================================
     CARDS
  ========================================================= */

  const [cards, setCards] = useState([]);

  /* =========================================================
     FLIP
  ========================================================= */

  const [isFlipped, setIsFlipped] = useState(false);

  const [allCardVoiceEnabled, setAllCardVoiceEnabled] = useState(false);

  const [autoPlayEnabled, setAutoPlayEnabled] = useState(false);

  const [isSessionResetting, setIsSessionResetting] = useState(false);

  /* =========================================================
     DRAG
  ========================================================= */

  const [isDragging, setIsDragging] = useState(false);

  const [dragX, setDragX] = useState(0);

  const [dragY, setDragY] = useState(0);

  /* =========================================================
     DRAG DIRECTION

     null       = no drag
     learning   = left
     known      = right
  ========================================================= */

  const [dragDirection, setDragDirection] = useState(null);

  /* =========================================================
     COUNTERS
  ========================================================= */

  const [learningCount, setLearningCount] = useState(0);

  const [knownCount, setKnownCount] = useState(0);

  /* =========================================================
     TIMER
  ========================================================= */

  const [elapsedSeconds, setElapsedSeconds] = useState(27);

  /* =========================================================
     POINTER REFERENCE
  ========================================================= */

  const pointerStart = useRef({
    x: 0,
    y: 0,
  });

  const dragged = useRef(false);

  /* =========================================================
     CURRENT CARD
  ========================================================= */

  const currentCard = cards[0];
  const currentCardId = Number(currentCard?.id || 0);
  const knownCardIds = useRef(new Set());

  /* =========================================================
     TIMER
  ========================================================= */

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedSeconds((previous) => previous + 1);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    persistQuestionBankFlowState(flowState);
  }, [flowState]);

  useEffect(() => {
    setLearningCount(Number(summary.still_learning || 0));
    setKnownCount(Number(summary.know || 0));
  }, [summary.know, summary.still_learning]);

  useEffect(() => {
    const apiCards = deckQuery.data?.pages.flatMap((page) => page?.payload?.flash_cards_list || []) || [];
    if (!apiCards.length) return;
    setCards((existing) => {
      const existingIds = new Set(existing.map((card) => card.id));
      const nextCards = apiCards.filter((card) => !existingIds.has(Number(card.flash_card_id))).map((card, index) => ({
        id: Number(card.flash_card_id),
        index: Number(card.flash_card_index || index + 1),
        front: card.name || "",
        back: card.description || "",
        color: flashCardData[index % flashCardData.length]?.color || "cyan",
        bookmarkStatus: Number(card.bookmark_status || 0),
        freePaid: card.free_paid,
        paymentStatus: Number(card.payment_status || 0),
        subscriptionMessage: card.subscription_message || "",
      }));
      return nextCards.length ? [...existing, ...nextCards] : existing;
    });
  }, [deckQuery.data]);

  useEffect(() => {
    if (!currentCardId || knownCardIds.current.has(currentCardId)) return;
    knownCardIds.current.add(currentCardId);
    const saveView = () => storeView.mutate({ flash_card_id: currentCardId, subject_id: subjectId, timer_seconds: Math.max(1, elapsedSeconds), is_view: 0, quest_category_id: 9 });
    storeView.mutate({ flash_card_id: currentCardId, subject_id: subjectId, timer_seconds: 1, is_view: 1, quest_category_id: 9 });
    const interval = window.setInterval(saveView, 15000);
    return () => { window.clearInterval(interval); saveView(); };
  }, [currentCardId, storeView.mutate, subjectId]);

  /* =========================================================
     FORMAT TIMER
  ========================================================= */

  const formattedTime = () => {
    const minutes = Math.floor(elapsedSeconds / 60)
      .toString()
      .padStart(2, "0");

    const seconds = (elapsedSeconds % 60).toString().padStart(2, "0");

    return `00:${minutes}:${seconds}`;
  };

  /* =========================================================
     RESET DRAG
  ========================================================= */

  const resetDrag = () => {
    setDragX(0);

    setDragY(0);

    setIsDragging(false);

    setDragDirection(null);
  };

  /* =========================================================
     NEXT CARD
  ========================================================= */

  const showNextCard = () => {
    setCards((previousCards) => {
      if (previousCards.length <= 1) {
        return previousCards;
      }

      return [...previousCards.slice(1), previousCards[0]];
    });

    setIsFlipped(false);

    resetDrag();
  };

  const saveReadStatus = async (readStatus) => {
    if (!currentCardId || markRead.isPending) return;
    if (currentCard?.freePaid === "P" && currentCard?.paymentStatus !== 1) return;
    try {
      await markRead.mutateAsync({ flash_card_id: currentCardId, subject_id: subjectId, read_status: readStatus });
    } catch (_error) {
      return;
    }
    if (readStatus) setKnownCount((value) => value + 1);
    else setLearningCount((value) => value + 1);
    showNextCard();
  };

  const speakCurrentCard = () => {
    if (!currentCard || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const text = isFlipped ? currentCard.back : currentCard.front;
    speakWithIndianVoices(text);
  };

  useEffect(() => {
    if (!allCardVoiceEnabled || autoPlayEnabled || !currentCard || !window.speechSynthesis) return undefined;

    window.speechSynthesis.cancel();
    const text = isFlipped ? currentCard.back : currentCard.front;
    speakWithIndianVoices(text);

    return () => window.speechSynthesis.cancel();
  }, [allCardVoiceEnabled, autoPlayEnabled, currentCardId, isFlipped]);

  useEffect(() => {
    if (!autoPlayEnabled || !currentCardId) return undefined;
    if (currentCard?.freePaid === "P" && currentCard?.paymentStatus !== 1) {
      setAutoPlayEnabled(false);
      return undefined;
    }

    let cancelled = false;
    let flipTimer;
    let advanceTimer;
    const advanceCard = async () => {
      if (cancelled) return;
      setDragDirection("known");
      setDragX(Math.max(window.innerWidth, 720));
      setDragY(-24);

      // Let the active card leave the stack before advancing the API-backed deck.
      await new Promise((resolve) => {
        advanceTimer = window.setTimeout(resolve, 360);
      });
      if (cancelled) return;

      try {
        await markRead.mutateAsync({ flash_card_id: currentCardId, subject_id: subjectId, read_status: 1 });
        if (cancelled) return;
        setKnownCount((value) => value + 1);
        showNextCard();
      } catch (_error) {
        if (!cancelled) setAutoPlayEnabled(false);
      }
    };

    const speakThen = (text, onComplete) => {
      const wordCount = String(text || "").trim().split(/\s+/).filter(Boolean).length;
      const minimumReadingMs = Math.max(1800, wordCount * 400);
      const startedAt = Date.now();
      const finishAfterMinimumReadTime = () => {
        const remainingMs = Math.max(0, minimumReadingMs - (Date.now() - startedAt));
        advanceTimer = window.setTimeout(() => {
          if (!cancelled) onComplete();
        }, remainingMs);
      };
      speakWithIndianVoices(text, {
        onComplete: () => { if (!cancelled) finishAfterMinimumReadTime(); },
        onError: () => { if (!cancelled) setAutoPlayEnabled(false); },
      });
    };

    setIsFlipped(false);
    if (allCardVoiceEnabled && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      speakThen(currentCard.front, () => {
        setIsFlipped(true);
        flipTimer = window.setTimeout(() => speakThen(currentCard.back, advanceCard), 200);
      });
    } else {
      flipTimer = window.setTimeout(() => setIsFlipped(true), 3500);
      advanceTimer = window.setTimeout(advanceCard, 7000);
    }

    return () => {
      cancelled = true;
      window.clearTimeout(flipTimer);
      window.clearTimeout(advanceTimer);
      if (allCardVoiceEnabled && window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [allCardVoiceEnabled, autoPlayEnabled, currentCardId]);

  const toggleBookmark = async (event) => {
    event.stopPropagation();
    if (!currentCardId) return;
    const result = await bookmark.mutateAsync({ flash_card_id: currentCardId });
    setCards((existing) => existing.map((card) => card.id === currentCardId ? { ...card, bookmarkStatus: Number(result?.payload?.bookmark_status || 0) } : card));
  };

  const restartSession = () => {
    setAutoPlayEnabled(false);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsFlipped(false);
    resetDrag();
    setCards((existing) => [...existing].sort((first, second) => first.index - second.index));
    setIsSessionResetting(true);
    window.setTimeout(() => setIsSessionResetting(false), 520);
  };

  const showPreviousCard = () => {
    setCards((previousCards) => {
      if (previousCards.length <= 1) return previousCards;
      return [previousCards[previousCards.length - 1], ...previousCards.slice(0, -1)];
    });
    setIsFlipped(false);
    resetDrag();
  };

  /* =========================================================
     POINTER DOWN
  ========================================================= */

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }

    pointerStart.current = {
      x: event.clientX,
      y: event.clientY,
    };

    dragged.current = false;

    setDragDirection(null);

    setIsDragging(true);

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (error) {
      // Ignore pointer capture error.
    }
  };

  /* =========================================================
     POINTER MOVE
  ========================================================= */

  const handlePointerMove = (event) => {
    if (!isDragging) {
      return;
    }

    const currentX = event.clientX - pointerStart.current.x;

    const currentY = event.clientY - pointerStart.current.y;

    /* =====================================================
       MARK AS DRAGGED
    ===================================================== */

    if (Math.abs(currentX) > 5 || Math.abs(currentY) > 5) {
      dragged.current = true;
    }

    /* =====================================================
       MOVE CARD
    ===================================================== */

    setDragX(currentX);

    setDragY(currentY * 0.2);

    /* =====================================================
       LEFT = LEARNING
    ===================================================== */

    if (currentX < -8) {
      setDragDirection("learning");
    } else if (currentX > 8) {
      /* =====================================================
       RIGHT = KNOWN
    ===================================================== */
      setDragDirection("known");
    } else {
      /* =====================================================
       CENTER
    ===================================================== */
      setDragDirection(null);
    }
  };

  /* =========================================================
     POINTER UP
  ========================================================= */

  const handlePointerUp = () => {
    if (!isDragging) {
      return;
    }

    const swipeThreshold = 90;

    /* =====================================================
       LEFT SWIPE
       LEARNING
    ===================================================== */

    if (dragX <= -swipeThreshold) {
      saveReadStatus(0);

      return;
    }

    /* =====================================================
       RIGHT SWIPE
       KNOWN
    ===================================================== */

    if (dragX >= swipeThreshold) {
      saveReadStatus(1);

      return;
    }

    /* =====================================================
       CLICK = FLIP
    ===================================================== */

    if (!dragged.current) {
      setIsFlipped((previous) => !previous);
    }

    resetDrag();
  };

  /* =========================================================
     POINTER CANCEL
  ========================================================= */

  const handlePointerCancel = () => {
    resetDrag();
  };

  /* =========================================================
     MAIN CARD STYLE
  ========================================================= */

  const mainCardStyle = {
    transform: `
      translate3d(
        ${dragX}px,
        ${dragY}px,
        0
      )
      rotate(
        ${dragX * 0.035}deg
      )
    `,

    transition: isDragging ? "none" : "transform 0.3s ease",

    zIndex: 20,
  };

  return (
    <>
      {/* =====================================================
          PAGE OVERRIDE
      ===================================================== */}

      <style>
        {`
          .homeDashboard-wrapper .page-content {
            padding-right: 0;
          }
        `}
      </style>

      <HomeDashboardLayout showHeader={false}>
        <div
          className="
            qbl-page
            qbankList-page
            podcastListt
          "
        >
          <div className="qbl-main" aria-label="Question bank content">
            <section className="qbl-panel">
              {/* =================================================
                  HEADER
              ================================================= */}

              <div
                className="
                  right-notify
                  d-flex
                  justify-content-between
                  align-items-center
                "
              >
                {/* BACK */}

                <div>
                  <button
                    type="button"
                    className="
                      categories-back-button
                    "
                    onClick={() => flowState?.qbankBackTarget?.pathname ? navigate(flowState.qbankBackTarget.pathname, { state: flowState.qbankBackTarget.state }) : navigate(FLASH_CARD_ROUTES.start, { state: flowState })}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>

                {/* TITLE */}

                <div>
                  {/* TIMER */}

                  <div
                    className="
                          flashcard-bottom-timer
                        "
                  >
                    <div
                      className="
                            flashcard-bottom-timer__icon
                          "
                    >
                      <i
                        className="
                              bi
                              bi-stopwatch
                            "
                      />
                    </div>

                    <div
                      className="
                            flashcard-bottom-timer__content
                          "
                    >
                      <span
                        className="
                              flashcard-bottom-timer__label
                            "
                      >
                        Time
                      </span>

                      <strong
                        className="
                              flashcard-bottom-timer__value
                            "
                      >
                        {formattedTime()}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* PROFILE */}

                <div>
                  <HeaderProfileSection />
                </div>
              </div>

              {/* =================================================
                  BODY
              ================================================= */}

              <div className="qb-body">
                {/* TITLE */}
                <div className="card-flex-data">
                  <div>
                    <h1 className="qb-title">
                      {cardProgress ? cardProgress.replaceAll("_", " ") : "All"} <span>Cards</span>
                    </h1>

                    <p className="qb-sub mb-1">
                      Card {currentCard?.index || 0} of {deckQuery.data?.pages?.[0]?.payload?.pagination?.totalRecords || cards.length}
                    </p>
                  </div>

                  <div
                    className="
                        flashcard-bottom-controls
                      "
                  >
                    <div
                      className="
                          flashcard-control-group
                        "
                    >
                      <button
                        type="button"
                        className="
                            flashcard-bottom-btn
                            flashcard-bottom-btn--undo
                          "
                        aria-label="Show previous Flash Card"
                        onClick={showPreviousCard}
                      >
                        <i
                          className="
                              bi
                              bi-arrow-counterclockwise
                            "
                        />
                      </button>

                      <button
                        type="button"
                        className={`
                            flashcard-bottom-btn
                            flashcard-bottom-btn--sound
                            ${allCardVoiceEnabled ? "flashcard-bottom-btn--active" : ""}
                          `}
                        aria-label={allCardVoiceEnabled ? "Disable all-card voice" : "Enable all-card voice"}
                        aria-pressed={allCardVoiceEnabled}
                        onClick={() => setAllCardVoiceEnabled((enabled) => !enabled)}
                      >
                        <i
                          className="
                              bi
                              bi-volume-up
                            "
                        />
                      </button>

                      <button
                        type="button"
                        className={`
                            flashcard-bottom-btn
                            flashcard-bottom-btn--play
                            ${autoPlayEnabled ? "flashcard-bottom-btn--active" : ""}
                          `}
                        aria-label={autoPlayEnabled ? "Pause autoplay" : "Start autoplay"}
                        aria-pressed={autoPlayEnabled}
                        onClick={() => setAutoPlayEnabled((enabled) => !enabled)}
                      >
                        <i
                          className={`
                              bi
                              ${autoPlayEnabled ? "bi-pause-fill" : "bi-play-fill"}
                            `}
                        />
                      </button>

                      <button
                        type="button"
                        className="flashcard-bottom-btn flashcard-bottom-btn--reset"
                        aria-label="Restart Flash Card session"
                        onClick={restartSession}
                      >
                        <i className="bi bi-arrow-clockwise" />
                      </button>

                      <button
                        type="button"
                        className="
                            flashcard-bottom-btn
                            flashcard-bottom-btn--shuffle
                          "
                        aria-label="Flash Card settings"
                      >
                        <i
                          className="
                              bi
                              bi-gear
                            "
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    PROGRESS
                ================================================= */}

                <div
                  className="
                    flashcard-progress-wrapper
                  "
                >
                  <div
                    className="
                      flashcard-progress-section
                    "
                  >
                    <div
                      className="
                        flashcard-progress-header
                      "
                    >
                      <span>Progress</span>

                      <span>{progress}%</span>
                    </div>

                    <div
                      className="
                        flashcard-progress-bar
                      "
                    >
                      <div
                        className="
                          flashcard-progress-bar__fill
                        "
                        style={{
                          width: `${Math.min(100, Math.max(0, progress))}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* =================================================
                      LEARNING / KNOWN
                  ================================================= */}

                  <div
                    className="
                      flashcard-learning-section
                    "
                  >
                    {/* =================================================
                        LEARNING
                    ================================================= */}

                    <div
                      className={`
                        flashcard-learning-item
                        flashcard-learning-item--learning
                        ${
                          dragDirection === "learning"
                            ? "flashcard-learning-item--active"
                            : ""
                        }
                      `}
                    >
                      <div
                        className="
                          flashcard-learning-left
                        "
                      >
                        <span
                          className="
                            flashcard-learning-icon
                            flashcard-learning-icon--learning
                          "
                        >
                          <i className="bi bi-x"></i>
                        </span>

                        <span
                          className="
                            flashcard-learning-label
                          "
                        >
                          Learning
                        </span>
                      </div>

                      <strong
                        className="
                          flashcard-learning-count
                        "
                      >
                        {learningCount}
                      </strong>
                    </div>

                    {/* DIVIDER */}

                    <div
                      className="
                        flashcard-learning-divider
                      "
                    />

                    {/* =================================================
                        KNOWN
                    ================================================= */}

                    <div
                      className={`
                        flashcard-learning-item
                        flashcard-learning-item--known
                        ${
                          dragDirection === "known"
                            ? "flashcard-learning-item--active"
                            : ""
                        }
                      `}
                    >
                      <div
                        className="
                          flashcard-learning-left
                        "
                      >
                        <span
                          className="
                            flashcard-learning-icon
                            flashcard-learning-icon--known
                          "
                        >
                          <i className="bi bi-check2"></i>
                        </span>

                        <span
                          className="
                            flashcard-learning-label
                          "
                        >
                          Known
                        </span>
                      </div>

                      <strong
                        className="
                          flashcard-learning-count
                        "
                      >
                        {knownCount}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* =================================================
                    FLASHCARD
                ================================================= */}

                <div
                  className="
                    flashcard-page-body
                  "
                >
                  <div
                    className="
                      flashcard-page-container
                    "
                  >
                    {/* =================================================
                        STACK
                    ================================================= */}

                    {deckQuery.isLoading && !cards.length ? <FlashCardDeckShimmer /> : <div
                      className={`flashcard-stack${isSessionResetting ? " flashcard-stack--resetting" : ""}`}
                    >
                      {cards.slice(0, 2).map((card, index) => {
                        const isMain = index === 0;

                        return (
                          <div
                            key={card.id}
                            className={`
                                  flashcard-stack-card
                                  flashcard-stack-card--${card.color}
                                  ${
                                    isMain
                                      ? "flashcard-stack-card--main"
                                      : "flashcard-stack-card--behind"
                                  }
                                `}
                            style={isMain ? mainCardStyle : undefined}
                            onPointerDown={
                              isMain ? handlePointerDown : undefined
                            }
                            onPointerMove={
                              isMain ? handlePointerMove : undefined
                            }
                            onPointerUp={isMain ? handlePointerUp : undefined}
                            onPointerCancel={
                              isMain ? handlePointerCancel : undefined
                            }
                          >
                            <div
                              className={`
                                    flashcard-stack-card__inner
                                    ${
                                      isMain && isFlipped
                                        ? "flashcard-stack-card__inner--flipped"
                                        : ""
                                    }
                                  `}
                            >
                              {/* =================================================
                                      FRONT
                                  ================================================= */}

                              <div
                                className="
                                      flashcard-stack-face
                                      flashcard-stack-face--front
                                    "
                              >
                                <div
                                  className="
                                        flashcard-stack-top
                                      "
                                >
                                  <span
                                    className="
                                          flashcard-stack-side
                                        "
                                  >
                                    Front side
                                  </span>

                                  <div
                                    className="
                                          flashcard-stack-actions
                                        "
                                  >
                                    <button
                                      type="button"
                                      className="
                                            flashcard-stack-action
                                          "
                                      onPointerDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={speakCurrentCard}
                                    >
                                      <i className="bi bi-volume-up"></i>
                                    </button>

                                    <button
                                      type="button"
                                      className="
                                            flashcard-stack-action
                                          "
                                      onPointerDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={toggleBookmark}
                                    >
                                      <i className={card.bookmarkStatus ? "bi bi-bookmark-fill" : "bi bi-bookmark"}></i>
                                    </button>
                                  </div>
                                </div>

                                <div
                                  className="
                                        flashcard-stack-content
                                      "
                                >
                                  <p>{card.front}</p>
                                </div>

                                <div
                                  className="
                                        flashcard-stack-footer
                                      "
                                >
                                  <i
                                    className="
                                          bi
                                          bi-hand-index-thumb
                                        "
                                  />

                                  <span>
                                    Tap card to flip, swipe to continue
                                  </span>
                                </div>
                              </div>

                              {/* =================================================
                                      BACK
                                  ================================================= */}

                              <div
                                className="
                                      flashcard-stack-face
                                      flashcard-stack-face--back
                                    "
                              >
                                <div
                                  className="
                                        flashcard-stack-top
                                      "
                                >
                                  <span
                                    className="
                                          flashcard-stack-side
                                        "
                                  >
                                    Back side
                                  </span>

                                  <div
                                    className="
                                          flashcard-stack-actions
                                        "
                                  >
                                    <button
                                      type="button"
                                      className="
                                            flashcard-stack-action
                                          "
                                      onPointerDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={speakCurrentCard}
                                    >
                                      <i className="bi bi-volume-up"></i>
                                    </button>

                                    <button
                                      type="button"
                                      className="
                                            flashcard-stack-action
                                          "
                                      onPointerDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={toggleBookmark}
                                    >
                                      <i className={card.bookmarkStatus ? "bi bi-bookmark-fill" : "bi bi-bookmark"}></i>
                                    </button>
                                  </div>
                                </div>

                                <div
                                  className="
                                        flashcard-stack-content
                                      "
                                >
                                  <p>{card.back}</p>
                                </div>

                                <div
                                  className="
                                        flashcard-stack-footer
                                      "
                                >
                                  <i
                                    className="
                                          bi
                                          bi-hand-index-thumb
                                        "
                                  />

                                  <span>
                                    Tap card to flip, swipe to continue
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>}

                    {/* =================================================
                        BOTTOM CONTROLS
                    ================================================= */}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default FlashCard;
